import { makeStyles, useTheme } from '@material-ui/core/styles'
import AppLinks from './AppLinks'
import Button from '@material-ui/core/Button'
import clsx from 'clsx'
import IconLinks from './IconLinks'
import News from './News'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'

const useStyles = makeStyles(theme => ({
  button: {
    backgroundColor: '#ffff00',
    marginBottom: theme.spacing(6),
    marginTop: theme.spacing(2),
    '&:disabled': {
      backgroundColor: 'DimGray',
      color: 'LightGray',
      textDecoration: 'line-through'
    }
  },
  content: {
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
    paddingTop: theme.spacing(6),
    '&.small': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  ellipsis: {
    height: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  maintenance: {
    alignItems: 'flex-start',
    background: 'linear-gradient(90deg, #d32f2f, #ef6c00)',
    borderRadius: 4,
    boxShadow: '0 6px 18px rgba(0,0,0,0.15)',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    margin: theme.spacing(0, -6, 3),
    padding: theme.spacing(1.5, 3),
    width: `calc(100% + ${theme.spacing(12)}px)`,
    '& strong': { fontWeight: 800 },
    '& .dates': { fontWeight: 700 },
    '& .note': {},
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(0, -2, 3),
      padding: theme.spacing(1.5, 2),
      width: `calc(100% + ${theme.spacing(4)}px)`
    }
  },
  inner: {
    margin: '0 auto 0 auto',
    maxWidth: '1400px',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: '90%',
    zIndex: 1
  },
  links: {
    marginBottom: theme.spacing(5),
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    marginTop: theme.spacing(6),
    maxWidth: '1400px',
    position: 'relative',
    '&.small': {
      marginBottom: 0,
      marginLeft: theme.spacing(0),
      marginRight: theme.spacing(0)
    }
  },
  myTime: {
    color: '#fff',
    fontSize: '32pt',
    fontWeight: 700,
    '&.small': {
      fontSize: '26pt'
    }
  },
  outer: {
    background: 'linear-gradient(#003d6e 100px, #1d6092 400px)',
    backgroundColor: '#003d6e',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    '&::after': {
      background: 'transparent url("/img/mytime.svg") no-repeat left top',
      backgroundPosition: '90%, 0',
      backgroundSize: '60% auto',
      content: '""',
      height: '100%',
      opacity: 0.1,
      position: 'fixed',
      right: '-60px',
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        backgroundPosition: '60%, 0',
        backgroundSize: '120% auto',
        right: '-16px'
      }
    }
  },
  welcome: {
    color: '#fff',
    fontSize: '9pt',
    fontWeight: 400
  }
}))

const urls = {
  urlTest: 'https://shb.ais.ucla.edu/shibboleth-idp/profile/SAML2/Unsolicited/SSO?providerId=https://test.kronos.ucla.edu/shibboleth',
  urlProd: 'https://shb.ais.ucla.edu/shibboleth-idp/profile/SAML2/Unsolicited/SSO?providerId=https://kronos.ucla.edu/shibboleth',
  urlDev: 'https://shb.ais.ucla.edu/shibboleth-idp/profile/SAML2/Unsolicited/SSO?providerId=https://dev.kronos.ucla.edu/shibboleth',
  urlStage: 'https://shb.ais.ucla.edu/shibboleth-idp/profile/SAML2/Unsolicited/SSO?providerId=https://stage.kronos.ucla.edu/shibboleth'
}

const parseProperties = (text = '') => {
  const result = {}
  text.split('\n').forEach(line => {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#')) return
    const eq = trimmed.indexOf('=')
    if (eq === -1) return
    const key = trimmed.slice(0, eq).trim()
    const value = trimmed.slice(eq + 1).trim()
    result[key] = value
  })
  return result
}

export default function Headline (props) {
  const classes = useStyles()

  const theme = useTheme()

  const small = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true })

  const [maintenance, setMaintenance] = useState({ active: false })
  const [environment, setEnvironment] = useState('prod')

  useEffect(() => {
    let mounted = true
    fetch('/config/app.properties', { cache: 'no-store' })
      .then(res => res.ok ? res.text() : null)
      .then(text => {
        if (!mounted || !text) return
        const parsed = parseProperties(text)
        const isActive = String(parsed.active || '').toLowerCase() === 'true'
        const env = (parsed.environment || 'prod').toLowerCase()
        if (mounted) {
          if (env === 'test') setEnvironment('test')
          else if (env === 'dev') setEnvironment('dev')
          else if (env === 'stage') setEnvironment('stage')
          else setEnvironment('prod')
        }
        if (mounted && isActive) {
          setMaintenance({
            active: true,
            start: parsed.start || '',
            end: parsed.end || '',
            date_and_time: parsed.date_and_time || '',
            message: parsed.message || 'Sign-in may be unavailable during this window.'
          })
        } else if (mounted) {
          setMaintenance({ active: false })
        }
      })
      .catch(() => {})
    return () => { mounted = false }
  }, [])

  const onClick = () => {
    let target = urls.urlProd
    if (environment === 'prod') target = urls.urlProd
    else if (environment === 'dev') target = urls.urlDev
    else if (environment === 'stage') target = urls.urlStage
    else if (environment === 'test') target = urls.urlTest
    window.location.href = target
  }

  return (
    <div className={classes.outer}>
      <div className={classes.inner}>
        <div className={clsx(classes.content, {
          small: small
        })}>
          <News active={false} />
          {maintenance.active && (
            <div className={classes.maintenance} role="status" aria-live="polite">
              <Typography variant="body1" component="div">
                <strong>Scheduled System Maintenance</strong>
              </Typography>
              <Typography variant="body2" component="div" >
                Due to regularly scheduled maintenance, there will be a planned Kronos Web Interface outage on <strong><u>{maintenance.date_and_time}</u></strong> During this time, users will be unable to timestamp, view, or edit timecards within mytime.ucla.edu. Please note, Kronos clocks will remain active and continue to collect punches as normal during this timeframe.
              </Typography> 
              <Typography variant="body2" component="div" >
                Timestamps that are missed during this outage should be entered manually by the supervisor once the outage is over. Please coordinate accordingly with your supervisor.
              </Typography>
            </div>
          )}
          <Typography
            classes={{ root: classes.welcome }}
            variant="h6"
            component="h1"
            gutterBottom
          >
            <img
              alt=""
              aria-hidden="true"
              className={classes.ellipsis}
              src="/img/ellipsis.svg"
            />WELCOME TO
          </Typography>
          <Typography
            className={clsx(classes.myTime, {
              small: small === true
            })}
            variant="h4"
            component="h1"
            gutterBottom
          >
            MyTime
          </Typography>
          <Button
            classes={{
              root: classes.button,
              disabled: classes.disabled
            }}
            disabled={false}
            onClick={onClick}
            size="large"
            variant="contained"
          >
            Sign In With UCLA ID
          </Button>
          <AppLinks />
        </div>
        <div className={clsx(classes.links, {
          small: small === true
        })}>
          <IconLinks
            handleMenuItem={props.handleMenuItem}
            menuItems={props.menuItems}
          />
        </div>
      </div>
    </div>
  )
}
Headline.propTypes = {
  handleMenuItem: PropTypes.func,
  menuItems: PropTypes.array,
  setOpenMenu: PropTypes.func
}
